import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'

import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { Button } from '@wartek-id/button-v1'
import { Select } from '@wartek-id/select'

import { Header } from 'app/KinerjaShared/components/Header'
import SurveyPendampinganSuccess from 'app/KinerjaKSPage/pages/Pelaksanaan/SurveyPendampingan/SurveyPendampinganSuccess'
import { PENGELOLAAN_KINERJA_KS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import {
  TablePelaksanaanKinerja,
  TableHeader,
  TableHeaderItem,
  TableBody,
  TableRow,
  TableRowItem,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import { PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_SURVEY_CRITERIAS } from 'app/KinerjaTimPenilaiPSPage/constants/praktikKinerja'
import {
  useAPIPostSubmitSurveyPendampingan,
  useAPIGetSurveyPendampingan,
} from 'api/kinerja/pengawas-sekolah/pelaksanaan'
import { LoadingState } from 'components/LoadingState'
import { useAuth } from 'utils/auth'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'

import type { TPraktikKinerjaSurveyPendampinganRating } from 'types/kinerja-ps-pegawai'

const SURVEY_ITEMS: Record<string, string> = {
  1: PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_SURVEY_CRITERIAS[0],
  2: PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_SURVEY_CRITERIAS[1],
  3: PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_SURVEY_CRITERIAS[2],
  4: PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_SURVEY_CRITERIAS[3],
  5: PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_SURVEY_CRITERIAS[4],
}

type TFormSurveyPendampingan = {
  comment: string
  aspects: {
    [key: string]: string
  }
}

const RANGE_1_TO_8 = Array.from({ length: 8 }, (_, i) => i + 1)

const SurveyPendampingan = () => {
  const router = useRouter()
  const { id: skpPsId } = router.query as { id: string }
  const [hasSubmittedSurvey, setHasSubmittedSurvey] = useState(false)
  const { session } = useAuth()
  const { trackEvent } = usePPSTrackEvent(skpPsId)

  const {
    data: dataSurveyPendampingan,
    isLoading: loadingDataSurveyPendampingan,
  } = useAPIGetSurveyPendampingan({
    skpPsId,
  })

  const {
    mutate: submitSurveyPendampingan,
    isLoading: submitSurveyPendampinganLoading,
  } = useAPIPostSubmitSurveyPendampingan({
    skpPsId,
  })

  const methods = useForm<TFormSurveyPendampingan>({
    mode: 'onChange',
    defaultValues: {
      aspects: {
        1: '',
        2: '',
        3: '',
        4: '',
        5: '',
      },
      comment: '',
    },
  })

  useEffect(() => {
    try {
      const surveyRating: TPraktikKinerjaSurveyPendampinganRating = JSON.parse(
        dataSurveyPendampingan.rating
      )

      if (surveyRating.catatan !== '' && surveyRating.rating.length !== 0) {
        setHasSubmittedSurvey(true)
      }
    } catch (e) {}
  }, [dataSurveyPendampingan])

  const goToHome = () => {
    router.push(PENGELOLAAN_KINERJA_KS_PEGAWAI_PATH)
  }

  const onSubmit = (data: TFormSurveyPendampingan) => {
    const rating = Object.entries(data.aspects).map(([aspek, value]) => ({
      aspek: Number(aspek),
      value: Number(value),
    }))

    try {
      submitSurveyPendampingan({
        rating: JSON.stringify({
          catatan: data.comment,
          rating,
        }),
      })
      trackEvent('pelaksanaan kepala sekolah', 'submit survey', {
        email_ks: session?.user?.email ?? null,
      })
    } catch (e) {}
  }

  if (hasSubmittedSurvey) {
    return <SurveyPendampinganSuccess onClickBackToHome={goToHome} />
  }

  return (
    <div>
      <div className="max-w-5xl">
        <Header title="Survei Pendampingan Satuan Pendidikan" />
      </div>

      {loadingDataSurveyPendampingan ? (
        <LoadingState />
      ) : (
        <div className="max-w-5xl m-auto">
          <div className="flex items-start gap-2 p-4 border rounded-md mt-9 bg-surface-warning-subdued border-warning">
            <Icon>warning</Icon>
            <Text variant="body">
              <strong>Jika data Pengawas Sekolah tidak sesuai:</strong> Anda
              tidak perlu mengisi survei ini. Kabari Dinas Pendidikan di luar
              sistem.
            </Text>
          </div>

          <div className="p-6 mt-6 border border-b-0 rounded-t bg-blue-5 border-subdued">
            <Text variant="heading-lg" as="h2" className="mb-2">
              Beri rating terhadap pendampingan yang dilakukan Pengawas:{' '}
              {dataSurveyPendampingan.namaPengawas}
            </Text>
            <Text variant="body">
              Hasil survei dapat menjadi salah satu pertimbangan Tim Kinerja
              ketika melakukan pemantauan terhadap kinerja Pengawas.
            </Text>
          </div>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="py-6 bg-white border border-t rounded-b-lg border-subdued"
          >
            <div className="flex flex-col gap-6 px-10">
              <TablePelaksanaanKinerja className="!border !border-subdued">
                <TableHeader>
                  <TableHeaderItem className="!bg-white !border-r !border-r-subdued">
                    No
                  </TableHeaderItem>
                  <TableHeaderItem className="!bg-white !border-r !border-r-subdued">
                    Aspek
                  </TableHeaderItem>
                  <TableHeaderItem className="!bg-white !border-r !border-r-subdued text-center">
                    Rating
                    <Text variant="helper">
                      Beri angka 1 (terendah) hingga 8 (tertinggi)
                    </Text>
                  </TableHeaderItem>
                </TableHeader>
                <TableBody>
                  {Object.keys(SURVEY_ITEMS).map((value, index) => (
                    <TableRow
                      key={value}
                      className="!border-b !border-b-subdued"
                    >
                      <TableRowItem className="text-center w-12 !border-r !border-r-subdued">
                        {index + 1}
                      </TableRowItem>
                      <TableRowItem className='!border-r !border-r-subdued"'>
                        {SURVEY_ITEMS[value]}
                      </TableRowItem>
                      <TableRowItem className="text-center">
                        <Select
                          disabled={submitSurveyPendampinganLoading}
                          {...methods.register(`aspects.${value}`, {
                            required: true,
                          })}
                        >
                          <option
                            disabled
                            className="text-disabled"
                            selected
                            value=""
                          >
                            Pilih rating
                          </option>
                          {RANGE_1_TO_8.map((value) => (
                            <option key={value} value={value}>
                              {value}
                            </option>
                          ))}
                        </Select>
                      </TableRowItem>
                    </TableRow>
                  ))}
                </TableBody>
              </TablePelaksanaanKinerja>
              <div className="space-y-1">
                <Text as="label" htmlFor="comment">
                  Apa pengalaman selama pendampingan yang menunjukkan rating
                  Anda di atas?
                </Text>
                <Textarea
                  disabled={submitSurveyPendampinganLoading}
                  {...methods.register('comment', {
                    required: true,
                    maxLength: 250,
                  })}
                  id="comment"
                  maxLength={250}
                  rows={3}
                  placeholder="Ketik di sini"
                ></Textarea>
              </div>

              <div className="self-end justify-self-end">
                <Button
                  isLoading={submitSurveyPendampinganLoading}
                  type="submit"
                  disabled={
                    !methods.formState.isValid ||
                    submitSurveyPendampinganLoading
                  }
                >
                  Kirim survei ke Tim Kinerja
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}

export default SurveyPendampingan
